import {
  useClub,
  useMutationClubDetails,
  useQueryCustomPaymentCategory,
} from "@gymflow/api";
import { CustomPaymentCategoryDTO } from "@gymflow/types";
import { createColumnHelper } from "@tanstack/react-table";
import { usePageSize } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useGymflowModels from "../../../store";
import { LabeledSwitch, Spinner } from "../../atoms";
import { PaginatedTable } from "../../organisms/PaginatedTable";
import { SettingsContainer } from "../SettingsContainer";

export const PaymentMethodsTab = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { clubId } = useClubSettings();
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });
  const { api } = useGymflowModels();
  const columnHelper = createColumnHelper<CustomPaymentCategoryDTO>();

  const { data: dataClub, isFetching: isFetchingClub } = useClub({
    api,
    clubId,
  });
  const customPaymentsEnabled = dataClub?.customPaymentsEnabled ?? false;
  const {
    data: dataCustomPaymentCategory,
    isLoading: isLoadingCustomPaymentCategory,
  } = useQueryCustomPaymentCategory({ api, clubId });
  const {
    mutateAsync: mutateAsyncClubDetails,
    isLoading: isLoadingClubDetails,
  } = useMutationClubDetails({
    api,
  });

  const columns = [
    columnHelper.accessor("name", {
      header: () => t("page.settings.tab.paymentMethods.table.header.name"),
      cell: (column) => (
        <div className="text-sm font-medium text-gray-900">
          {column.getValue()}
        </div>
      ),
    }),
  ];

  return (
    <SettingsContainer
      title={t("page.settings.tab.paymentMethods.title")}
      subTitle={t("page.settings.tab.paymentMethods.subTitle")}
    >
      <PaginatedTable
        tableProps={{
          data: dataCustomPaymentCategory ?? [],
          columns: columns,
          isFetching: isLoadingCustomPaymentCategory,
          pageSize,
          tableContainerRef,
        }}
        aboveTable={
          <div className="relative p-6 pl-5">
            <LabeledSwitch
              label={t(
                "page.settings.tab.paymentMethods.inPersonPaymentsSwitch",
              )}
              value={customPaymentsEnabled}
              disabled={isFetchingClub || isLoadingClubDetails}
              onChange={async (checked) => {
                await mutateAsyncClubDetails({
                  clubId,
                  payload: { customPaymentsEnabled: checked },
                });
              }}
            />
            {(isFetchingClub || isLoadingClubDetails) && (
              <div className="bg-gray-0 absolute inset-0 flex items-center justify-center opacity-50">
                <Spinner />
              </div>
            )}
          </div>
        }
        // Right now we don't have pagination for the table
        hasNextPage={false}
        hasPreviousPage={false}
        goToNextPage={() => {
          setCurrentPage(currentPage + 1);
        }}
        goToPreviousPage={() => {
          setCurrentPage(currentPage - 1);
        }}
      />
    </SettingsContainer>
  );
};
